import { guard, raise, type Result } from '@/lib/result';
import { supabase } from '@/lib/supabase';

/**
 * Wraps Supabase auth so the rest of the app never imports the SDK for auth.
 * Phone numbers must already be E.164-normalized by the caller.
 */
export const authRepository = {
  async sendOtp(phone: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.auth.signInWithOtp({ phone });
      if (error) raise('server', error.message, error);
    });
  },

  async verifyOtp(phone: string, token: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.auth.verifyOtp({ phone, token, type: 'sms' });
      if (error) raise('server', error.message, error);
    });
  },

  /**
   * Dev-only: email+password sign-in for local testing without an SMS provider.
   * Tries to sign in; if the account doesn't exist yet, signs up (which returns
   * a session immediately when email confirmation is disabled).
   */
  async signInWithEmail(email: string, password: string): Promise<Result<void>> {
    return guard(async () => {
      const signIn = await supabase.auth.signInWithPassword({ email, password });
      if (!signIn.error) return;
      const signUp = await supabase.auth.signUp({ email, password });
      if (signUp.error) raise('server', signUp.error.message, signUp.error);
      if (!signUp.data.session) {
        raise('server', 'Email confirmation is required; disable it for dev testing.');
      }
    });
  },

  async signOut(): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.auth.signOut();
      if (error) raise('server', error.message, error);
    });
  },

  async getUserId(): Promise<string | null> {
    const { data } = await supabase.auth.getSession();
    return data.session?.user.id ?? null;
  },

  /** Subscribes to auth changes. Returns an unsubscribe function. */
  onAuthChange(callback: (userId: string | null) => void): () => void {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user.id ?? null);
    });
    return () => data.subscription.unsubscribe();
  },
};
